import { useMemo, useState, useCallback, useRef, useEffect } from "react";
import { parseDiagram, type Diagram, type DiagramNode } from "@/lib/diagramParser";
import { ZoomIn, ZoomOut, RotateCcw, Move } from "lucide-react";

interface DiagramCanvasProps {
  text: string;
  onChange?: (newText: string) => void;
}

interface Pos { x: number; y: number; }

export default function DiagramCanvas({ text, onChange }: DiagramCanvasProps) {
  const [zoom, setZoom] = useState(1);
  const [overrides, setOverrides] = useState<Record<string, Pos>>({});
  const [dragging, setDragging] = useState<{
    id: string; startSvgX: number; startSvgY: number; startNodeX: number; startNodeY: number;
  } | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const diagram = useMemo(() => {
    try { return parseDiagram(text); }
    catch { return { nodes: [], edges: [] } as Diagram; }
  }, [text]);

  // Reset overrides when diagram text changes (new parse)
  useEffect(() => { setOverrides({}); }, [text]);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    setZoom(z => Math.min(3, Math.max(0.3, z - e.deltaY * 0.001)));
  }, []);

  // Convert a mouse event to SVG coordinate space
  const toSvgCoords = (e: MouseEvent | React.MouseEvent): Pos | null => {
    const svg = svgRef.current;
    if (!svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return null;
    const svgPt = pt.matrixTransform(ctm.inverse());
    return { x: svgPt.x, y: svgPt.y };
  };

  const onNodeMouseDown = (e: React.MouseEvent, node: DiagramNode) => {
    e.stopPropagation();
    e.preventDefault();
    const svgPt = toSvgCoords(e);
    if (!svgPt) return;
    const nodePos = overrides[node.id] ?? { x: node.x, y: node.y };
    setDragging({ id: node.id, startSvgX: svgPt.x, startSvgY: svgPt.y, startNodeX: nodePos.x, startNodeY: nodePos.y });
  };

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: MouseEvent) => {
      const svgPt = toSvgCoords(e);
      if (!svgPt) return;
      setOverrides(prev => ({
        ...prev,
        [dragging.id]: {
          x: dragging.startNodeX + (svgPt.x - dragging.startSvgX),
          y: dragging.startNodeY + (svgPt.y - dragging.startSvgY),
        },
      }));
    };
    const onUp = () => {
      setDragging(null);
      if (!onChange) return;
      // Reorder text lines by the new Y position of the first node on each line
      const textLines = text.split("\n").map(l => l.trim()).filter(Boolean);
      // For each line, find the minimum Y of nodes belonging to that line
      const lineYMap = new Map<number, number>();
      for (const n of diagram.nodes) {
        const y = overrides[n.id]?.y ?? n.y;
        const existing = lineYMap.get(n.lineIndex);
        if (existing === undefined || y < existing) lineYMap.set(n.lineIndex, y);
      }
      const sortedLineIndices = Array.from(lineYMap.entries())
        .sort((a, b) => a[1] - b[1])
        .map(([idx]) => idx);
      const reordered = sortedLineIndices
        .filter(i => i < textLines.length)
        .map(i => textLines[i]);
      // Append any lines not tracked (shouldn't happen, safety net)
      const tracked = new Set(sortedLineIndices);
      textLines.forEach((_, i) => { if (!tracked.has(i)) reordered.push(textLines[i]); });
      onChange(reordered.join("\n"));
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, [dragging]);

  if (diagram.nodes.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground font-body text-sm">
        Type your flow on the left to see the diagram here
      </div>
    );
  }

  // Merge parsed positions with drag overrides
  const nodes = diagram.nodes.map(n => ({
    ...n,
    x: overrides[n.id]?.x ?? n.x,
    y: overrides[n.id]?.y ?? n.y,
  }));

  const padding = 60;
  const minX = Math.min(...nodes.map(n => n.x)) - padding;
  const minY = Math.min(...nodes.map(n => n.y)) - padding;
  const maxX = Math.max(...nodes.map(n => n.x + n.width)) + padding;
  const maxY = Math.max(...nodes.map(n => n.y + n.height)) + padding;

  const fullW = maxX - minX;
  const fullH = maxY - minY;
  const cx = minX + fullW / 2;
  const cy = minY + fullH / 2;
  const vbW = fullW / zoom;
  const vbH = fullH / zoom;
  const vbX = cx - vbW / 2;
  const vbY = cy - vbH / 2;

  const nodeById = new Map(nodes.map(n => [n.id, n]));

  return (
    <div className="relative w-full h-full rounded-xl overflow-hidden" onWheel={handleWheel}
      style={{ minHeight: 300, backgroundColor: "hsl(var(--diagram-bg))", cursor: dragging ? "grabbing" : "default" }}>

      {/* Zoom + hint controls */}
      <div className="absolute top-3 right-3 z-10 flex flex-col gap-1">
        {[
          { label: "+", action: () => setZoom(z => Math.min(3, z + 0.2)), icon: <ZoomIn className="h-3.5 w-3.5" /> },
          { label: "−", action: () => setZoom(z => Math.max(0.3, z - 0.2)), icon: <ZoomOut className="h-3.5 w-3.5" /> },
          { label: "↺", action: () => { setZoom(1); setOverrides({}); }, icon: <RotateCcw className="h-3 w-3" /> },
        ].map(btn => (
          <button
            key={btn.label}
            onClick={btn.action}
            title={btn.label === "↺" ? "Reset zoom & positions" : btn.label === "+" ? "Zoom in" : "Zoom out"}
            className="flex items-center justify-center h-7 w-7 rounded-md border border-border bg-background/90 hover:bg-muted text-muted-foreground hover:text-foreground transition-colors shadow-sm"
          >
            {btn.icon}
          </button>
        ))}
        <div className="text-center text-[10px] font-mono text-muted-foreground/60 mt-0.5">
          {Math.round(zoom * 100)}%
        </div>
      </div>

      {/* Drag hint */}
      <div className="absolute bottom-3 left-3 z-10 flex items-center gap-1 text-[10px] text-muted-foreground/50 select-none">
        <Move className="h-3 w-3" /> drag nodes to reorder
      </div>

      <svg
        ref={svgRef}
        viewBox={`${vbX} ${vbY} ${vbW} ${vbH}`}
        className="w-full h-full"
        style={{ minHeight: 300 }}
      >
        <defs>
          <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
            <polygon points="0 0, 10 3.5, 0 7" fill="hsl(var(--diagram-connector))" />
          </marker>
          <marker id="arrowhead-start" markerWidth="10" markerHeight="7" refX="1" refY="3.5" orient="auto-start-reverse">
            <polygon points="0 0, 10 3.5, 0 7" fill="hsl(var(--diagram-connector))" />
          </marker>
          <filter id="node-glow">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        {/* Edges — use merged positions */}
        {diagram.edges.map((edge, i) => {
          const from = nodeById.get(edge.from);
          const to = nodeById.get(edge.to);
          if (!from || !to) return null;

          const fromCx = from.x + from.width / 2;
          const fromCy = from.y + from.height / 2;
          const toCx = to.x + to.width / 2;
          const toCy = to.y + to.height / 2;

          const startY = fromCy < toCy ? from.y + from.height : from.y;
          const endY = fromCy < toCy ? to.y : to.y + to.height;
          const sameRow = Math.abs(from.y - to.y) < 10;
          const sx = sameRow ? (fromCx < toCx ? from.x + from.width : from.x) : fromCx;
          const sy = sameRow ? fromCy : startY;
          const ex = sameRow ? (fromCx < toCx ? to.x : to.x + to.width) : toCx;
          const ey = sameRow ? toCy : endY;
          const midY = (sy + ey) / 2;
          const path = sameRow
            ? `M ${sx} ${sy} L ${ex} ${ey}`
            : `M ${sx} ${sy} C ${sx} ${midY} ${ex} ${midY} ${ex} ${ey}`;

          return (
            <g key={i}>
              <path
                d={path} fill="none" stroke="hsl(var(--diagram-connector))" strokeWidth="2"
                markerEnd="url(#arrowhead)"
                markerStart={edge.bidirectional ? "url(#arrowhead-start)" : undefined}
              />
              {edge.label && (
                <text x={(sx + ex) / 2 + 8} y={(sy + ey) / 2 - 4}
                  fill="hsl(var(--diagram-highlight))" fontSize="11"
                  fontFamily="var(--font-mono)" fontWeight="500">
                  {edge.label}
                </text>
              )}
            </g>
          );
        })}

        {/* Nodes — draggable */}
        {nodes.map(node => (
          <NodeShape
            key={node.id}
            node={node}
            isDragging={dragging?.id === node.id}
            onMouseDown={(e) => onNodeMouseDown(e, node)}
          />
        ))}
      </svg>
    </div>
  );
}

function NodeShape({
  node,
  isDragging,
  onMouseDown,
}: {
  node: DiagramNode;
  isDragging: boolean;
  onMouseDown: (e: React.MouseEvent) => void;
}) {
  const cx = node.x + node.width / 2;
  const cy = node.y + node.height / 2;

  const shapeEl = (() => {
    switch (node.type) {
      case "start":
        return <rect x={node.x} y={node.y} width={node.width} height={node.height}
          rx={node.height / 2} fill="#314E6E" stroke="#5C7FAA" strokeWidth="2"
          filter="url(#node-glow)" />;
      case "decision":
        const hw = node.width / 2;
        const hh = node.height / 2 + 6;
        return <polygon points={`${cx},${cy - hh} ${cx + hw},${cy} ${cx},${cy + hh} ${cx - hw},${cy}`}
          fill="hsl(var(--diagram-node))" stroke="#A37B32" strokeWidth="2.5" />;
      case "io":
        const skew = 12;
        return <polygon
          points={`${node.x + skew},${node.y} ${node.x + node.width},${node.y} ${node.x + node.width - skew},${node.y + node.height} ${node.x},${node.y + node.height}`}
          fill="hsl(var(--diagram-node))" stroke="#4E6654" strokeWidth="2" />;
      default:
        return <rect x={node.x} y={node.y} width={node.width} height={node.height}
          rx={5} fill="hsl(var(--diagram-node))" stroke="hsl(var(--diagram-connector))" strokeWidth="1.5" />;
    }
  })();

  const textColor = node.type === "start" ? "#F7F4EE" : "hsl(var(--diagram-node-foreground))";

  return (
    <g
      onMouseDown={onMouseDown}
      style={{ cursor: isDragging ? "grabbing" : "grab" }}
      opacity={isDragging ? 0.85 : 1}
    >
      {shapeEl}
      <text x={cx} y={cy + 1} textAnchor="middle" dominantBaseline="central"
        fill={textColor} fontSize="12" fontFamily="var(--font-body)" fontWeight="500"
        style={{ userSelect: "none", pointerEvents: "none" }}>
        {node.label.length > 20 ? node.label.slice(0, 18) + "…" : node.label}
      </text>
    </g>
  );
}
